import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { OFACScreeningStatusEnum } from 'anchor-kyb-service/types';
import { Spinner, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GenericError from '~/components/GenericError/GenericError';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useLocationWithState } from '~/hooks/useLocationWithState';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { LatestOFACSanctionStatusModel } from '~/models';
import { isSubmittedInquiry } from '~/utils/persona-inquiry.utils';
export const GetAdditionalPaymentProcessorInfoPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            AdditionalPaymentProcessorInfoPageTID: {
                Loader: 'loading-ofac-data',
                Error: 'ofac-error',
                DetailsAddedToast: 'details-added'
            }
        }
    });
const { AdditionalPaymentProcessorInfoPageTID } = GetAdditionalPaymentProcessorInfoPageTID();
const renderLoader = ()=>/*#__PURE__*/ _jsx(Spinner, {
        testId: AdditionalPaymentProcessorInfoPageTID.Loader(),
        centeredInBox: true,
        centerBoxHeight: "100%"
    });
const renderError = ()=>/*#__PURE__*/ _jsx(GenericError, {
        testId: AdditionalPaymentProcessorInfoPageTID.Error()
    });
function RedirectToHome(param) {
    let { detailsAdded } = param;
    const { showSuccessToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    useOnMount(()=>{
        if (detailsAdded) {
            showSuccessToast(commonTexts.toasts.additionalDetailsAdded(), AdditionalPaymentProcessorInfoPageTID.DetailsAddedToast());
        }
    });
    return /*#__PURE__*/ _jsx(Navigate, {
        to: routes.home,
        replace: true
    });
}
function OpenPersonaInquiry(param) {
    let { inquiryId, sessionToken } = param;
    const { personaClient, browser: { locationReload } } = useIoContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [complete, setComplete] = useState(false);
    const persona = useMemo(()=>{
        return personaClient({
            onError: ()=>setError(true),
            onReady: ()=>setLoading(false),
            onComplete: ()=>setComplete(true),
            onCancel: ()=>locationReload()
        });
    }, []);
    useOnMount(()=>{
        persona.open(inquiryId, sessionToken);
    });
    if (error) return renderError();
    if (loading) return renderLoader();
    if (complete) return /*#__PURE__*/ _jsx(RedirectToHome, {
        detailsAdded: true
    });
    return null;
}
export async function fetchInquiry(param) {
    let { data, getUserStatus, getBusinessStatus, createInquiry } = param;
    const defaultStatus = new LatestOFACSanctionStatusModel({
        status: OFACScreeningStatusEnum.Cleared
    });
    const userStatusPromise = data.user.isSanctioned ? getUserStatus(data.user.id) : Promise.resolve(defaultStatus);
    const businessStatusPromise = data.business.isSanctioned ? getBusinessStatus(data.business.id) : Promise.resolve(defaultStatus);
    const [userStatus, businessStatus] = await Promise.all([
        userStatusPromise,
        businessStatusPromise
    ]);
    const inquiryPayload = {
        userId: userStatus.requiresInquiry() ? data.user.id : undefined,
        businessId: businessStatus.requiresInquiry() ? data.business.id : undefined
    };
    if (inquiryPayload.userId || inquiryPayload.businessId) {
        const inquiry = await createInquiry(inquiryPayload);
        return {
            inquiry
        };
    } else {
        return {
            inquiry: null
        };
    }
}
export default function AdditionalPaymentProcessorInfoPage() {
    const { state } = useLocationWithState();
    const { api } = useStoreActions();
    const renderInquiry = (param)=>{
        let { data } = param;
        return data.inquiry && !isSubmittedInquiry(data.inquiry.inquiryStatus) ? /*#__PURE__*/ _jsx(OpenPersonaInquiry, _object_spread({}, data.inquiry)) : /*#__PURE__*/ _jsx(RedirectToHome, {
            detailsAdded: true
        });
    };
    return state ? /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: async ()=>fetchInquiry({
                data: state.data,
                getUserStatus: (userId)=>api.ofacScreening.getUserStatus(userId),
                getBusinessStatus: (businessId)=>api.ofacScreening.getBusinessStatus(businessId),
                createInquiry: api.ofacScreening.createInquiry
            }),
        renderChild: renderInquiry,
        renderLoader: renderLoader,
        renderError: renderError
    }) : /*#__PURE__*/ _jsx(RedirectToHome, {});
}
